import React, { useEffect } from 'react';
import { styled, useStyletron } from 'baseui';
import { Theme } from '../../Themes/themeProvider/ThemeProvider';
import { MIcon } from '../Icon';
import { StyletronComponent } from 'styletron-react';
import { ComponentWithAs } from '../../Utilities/types/As';
import { SidebarGroupProps, SidebarItemProps } from '.';
import { STYLES, itemStyles, SidebarSizes } from './styles';
import { IDummyProps } from './types';
import { StatefulTooltip } from '../Tooltip';
import { ArrowDirection, FocusManager } from '../../Lib/FocusManager';
import { borderColor, borderWidth } from '../../Utilities/utils/Theming';
import * as meta from '../../meta-data.json';
import { LayerTooltip } from '../Layer';

const cloneWithProp = (child: React.ReactElement<any, string | React.JSXElementConstructor<any>>, prop: any) =>
	React.cloneElement(child, { ...prop });

export const SidebarGroup: React.FC<SidebarGroupProps> = ({ children, size, ...rest }) => {
	const [css] = useStyletron();

	if (React.Children.count(children) === 0) {
		return null;
	}

	return (
		<nav className={css(STYLES.GROUP)} {...rest}>
			<ul className={css(STYLES.GROUP_LIST)}>
				{React.Children.map(children || null, (child, i) => {
					if (!child) {
						return null;
					}
					return <li key={i}>{cloneWithProp(child, { size })}</li>;
				})}
			</ul>
		</nav>
	);
};

const StyledSidebarItem = styled<SidebarItemProps, 'a', typeof Theme>('a', itemStyles);
const StyledSidebarItemAsButton = styled<SidebarItemProps, 'button', typeof Theme>('button', itemStyles);

export const SidebarMultiItem = (props?: any) => {
	const [css] = useStyletron();
	return (
		<div
			className={css({
				position: 'relative',
				':before': {
					display: 'block',
					position: 'absolute',
					content: '" "',
					background: props.active
						? 'var(--epods--color-bg-overlays)'
						: 'var(--epods--color-bg-secondary-active)', //: 'var(--epods--color-bg-inactive-boxes-disabled)',
					height: '100%',
					width: '4px',
					top: '0',
					left: '0',
					zIndex: '1',
				},
			})}
		>
			{props.children}
		</div>
	);
};

const renderAsButtonOrNot = (as: string | React.ComponentClass<any, any> | React.FunctionComponent<any>) =>
	as === 'button' ? StyledSidebarItemAsButton : styled<SidebarItemProps, any, typeof Theme>(as, itemStyles);

export const SidebarItem: ComponentWithAs<any, SidebarItemProps> = ({
	as = 'a',
	hasTooltip = true,
	isCollapsible = true,
	icon,
	isactive,
	children,
	size = 'default',
	isContentPushed,
	asOverlay,
	hasSubitems,
	...rest
}) => {
	const [css] = useStyletron();
	const { isCollapsed } = React.useContext(SidebarContext);
	const Component: StyletronComponent<any> = as === 'a' ? StyledSidebarItem : renderAsButtonOrNot(as);

	const itemRef = React.useRef<any>();

	useEffect(() => {
		// @ts-ignore
		if (isactive == 'true') {
			itemRef.current?.focus();
		}
	}, [isactive]);

	const Item: React.FC = props => (
		<Component
			ref={itemRef}
			icon={icon}
			isactive={isactive}
			size={size}
			icononly={isCollapsible ? isCollapsible?.toString() : 'false'}
			{...(isactive === 'true' ? { 'aria-current': 'location' } : {})}
			data-epo="epo-sidebar-item"
			// TODO: we should be using a tooltip, but meanwhile, this
			// provides some help for mouse users.
			{...props} // {...(isCollapsed ? { title: children } : {})}
			{...rest}
		>
			{icon && typeof icon == 'string' ? (
				<MIcon
					icon={icon}
					size={'small'}
					className={css({
						...STYLES.ICON,
						height:
							`${SidebarSizes[size || 'default'].lineHeight} !important` ||
							'var(--epods--line-height-h5)',
						lineHeight: SidebarSizes[size || 'default'].lineHeight || 'var(--epods--line-height-h5)',
						...(isCollapsed ? {} : STYLES.ICON_WITH_TEXT),
					})}
				/>
			) : null}

			{icon && typeof icon !== 'string' ? (
				<div
					className={css({
						...STYLES.ICON,
						height:
							`${SidebarSizes[size || 'default'].lineHeight} !important` ||
							'var(--epods--line-height-h5)',
						lineHeight: SidebarSizes[size || 'default'].lineHeight || 'var(--epods--line-height-h5)',
						...(isCollapsed ? {} : STYLES.ICON_WITH_TEXT),
					})}
				>
					{icon}
				</div>
			) : null}

			<div
				// a11y-todo: ...if isCollapsed then add class "sr-only")
				className={`${isCollapsed && isCollapsible && 'sr-only'} ${css({
					...STYLES.LABEL,
					flexGrow: '1',
					color: 'inherit',
				})}`}
			>
				{children}
			</div>

			{hasSubitems && (
				<MIcon
					icon="navigate_next"
					className={css(
						isCollapsed
							? {
									position: 'absolute',
									right: '2px',
							  }
							: {},
					)}
				/>
			)}
		</Component>
	);

	if (hasTooltip && isCollapsed) {
		return (
			<LayerTooltip content={children} placement="right">
				<div>
					<Item />
				</div>
			</LayerTooltip>
		);
	}

	return <Item />;
};

const SidebarContext = React.createContext({
	isCollapsed: false,
});

function useDidUpdateEffect(fn: Function, inputs: Array<any>) {
	const didMountRef = React.useRef(false);

	React.useEffect(() => {
		if (didMountRef.current) fn();
		else didMountRef.current = true;
	}, [fn]);
}

export const CustomSidebar: React.FC<IDummyProps> = ({
	defaultIsCollapsed = true,
	bottomGroup,
	children,
	onIsCollapsedChange,
	widthExpanded = '380px',
	collapseButtonPlacement = 'bottom',
	hideCollapseButton = false,
	asOverlay = false,
	onChange,
	size = 'default',
	...rest
}) => {
	const [css] = useStyletron();
	const { className, ...restWithoutClassName } = rest;
	const [isCollapsed, setIsCollapsed] = React.useState(defaultIsCollapsed);
	const [isHoverActive, setIsHoverActive] = React.useState(asOverlay);
	const [isContentPushed, setIsContentPushed] = React.useState(!asOverlay);
	const collapseButton = React.useRef<any>();

	if (!children && !bottomGroup) {
		throw new Error('Sidebar needs to have either one of `children` or `bottomGroup` passed as prop.');
	}

	const onClickPinButton = (event: any) => {
		/**
		 * When hover is not active then content is pushed and the sidebar stays
		 */
		const collapseState = isHoverActive ? false : !isCollapsed;
		setIsCollapsed(collapseState);
		setIsHoverActive(false);
		onChange && onChange({ isCollapsed: collapseState, ...event });
		/**
		 * Needed only when content is pushed. e.g. You don't need to push if you don't
		 * have overlay active. Content is pushed by default
		 */
		if (asOverlay) {
			setIsContentPushed(!isContentPushed);
			onChange && onChange({ isCollapsed: isContentPushed, ...event });
		}
	};

	const onMouseEnter = (event: any) => {
		if (!isContentPushed) {
			setIsCollapsed(false);
			setIsHoverActive(true);
			onChange && onChange({ isCollapsed: false, ...event });
		}
	};

	const onMouseLeave = (event: any) => {
		if (!isContentPushed) {
			setIsCollapsed(true);
			setIsHoverActive(false);
			onChange && onChange({ isCollapsed: true, ...event });
		}
	};

	useDidUpdateEffect(() => {
		if (collapseButton.current) {
			// @ts-ignore
			//Array.from(collapseButton.current.childNodes)[0]?.focus();
		}
		onIsCollapsedChange && onIsCollapsedChange(isCollapsed);
	}, [isCollapsed]);

	useEffect(() => {
		setIsHoverActive(asOverlay);
		setIsContentPushed(!asOverlay);
	}, [asOverlay]);

	/**
	 * To create space for Pin button
	 */
	const sidebarContainerHeight = {
		height: asOverlay ? 'var(--epods--sidebar-overlay-' + size + '-saveheight)' : '100%',
	};
	const parentClassName = isContentPushed
		? css(sidebarContainerHeight)
		: css({
				position: 'relative',
				width: 'var(--epods--sidebar-overlay-' + size + '-collapsed-width)',
				...sidebarContainerHeight,
		  });

	const OverlayButton: StyletronComponent<any> = StyledSidebarItemAsButton;

	const contentShadow = asOverlay && !isCollapsed ? 'var(--epods--box-shadow-fifth-right)' : 'none';

	const overShadow = () => {
		return isContentPushed ? 'none' : contentShadow;
	};

	const cssVersion = `epo-sidebar-${meta.version}`;

	const renderChildrenWithProps = (children: any, props: any) =>
		React.Children.map(children, child => {
			if (React.isValidElement(child)) {
				return cloneWithProp(child, { ...props });
			}
			return child;
		});

	const iconNavigation = isCollapsed || isHoverActive ? 'expand_area' : 'collapsed_area';
	const navigationLabel = isCollapsed ? 'Expand' : 'Collapse';
	const iconType = isContentPushed ? 'collapsed_area' : 'expand_area';

	return (
		<SidebarContext.Provider
			value={{
				isCollapsed,
			}}
		>
			<FocusManager direction={ArrowDirection.UpDown} whitelistSelector={"[data-epo='epo-sidebar-item']"}>
				<div data-epo={cssVersion} className={parentClassName}>
					<div
						className={
							css({
								...STYLES.CONTAINER,
								width: isCollapsed ? 'auto' : widthExpanded,
								position: 'relative',
								overflow: 'visible',
								...borderColor('var(--epods--color-border-light)'),
								borderStyle: 'solid',
								...borderWidth('var(--epods--border-width)'),
								boxShadow: overShadow(),
							}) + ` ${className || ''}	`
						}
						onMouseEnter={onMouseEnter}
						onMouseLeave={onMouseLeave}
						{...restWithoutClassName}
					>
						{!asOverlay && !hideCollapseButton && collapseButtonPlacement === 'top' && (
							<div ref={collapseButton} className={css(STYLES.COLLAPSE_BUTTON_CONTAINER)}>
								<SidebarItem
									asOverlay={asOverlay}
									isContentPushed={isContentPushed}
									as="button"
									icon={iconNavigation}
									icononly={isCollapsed?.toString()}
									onClick={() => {
										setIsCollapsed(!isCollapsed);
										setIsHoverActive(false);
									}}
									aria-pressed={isCollapsed}
									aria-label="collapse"
									data-epo="epo-sidebar-collapse"
									size={size}
								>
									{navigationLabel}
								</SidebarItem>
							</div>
						)}
						<div className={css(STYLES.SCROLLABLE_CONTAINER)}>
							{renderChildrenWithProps(children, { size })}
						</div>
						<div className={css(STYLES.LAST_GROUP)}>
							{renderChildrenWithProps(bottomGroup, { size })}
							{!asOverlay && !hideCollapseButton && collapseButtonPlacement === 'bottom' && (
								<div ref={collapseButton} className={css(STYLES.COLLAPSE_BUTTON_CONTAINER)}>
									<SidebarItem
										asOverlay={asOverlay}
										isContentPushed={isContentPushed}
										as="button"
										icon={iconNavigation}
										icononly={isCollapsed?.toString()}
										onClick={onClickPinButton}
										aria-pressed={isCollapsed}
										aria-label="collapse"
										data-epo="epo-sidebar-collapse"
										size={size}
									>
										{navigationLabel}
									</SidebarItem>
								</div>
							)}
						</div>
					</div>
				</div>
				{asOverlay ? (
					<OverlayButton
						className={
							'border border-light border-top-0 ' +
							css({
								justifyContent: isContentPushed ? 'flex-end' : 'center',
								height: 'var(--epods--sidebar-overlay-' + size + '-button)',
							})
						}
						onClick={onClickPinButton}
						onMouseEnter={onMouseEnter}
						onMouseLeave={onMouseLeave}
						data-epo="epo-sidebar-30x"
					>
						<MIcon icon={iconType} color="var(--epods--color-text-primary)" size="small" />
					</OverlayButton>
				) : (
					<span></span>
				)}
			</FocusManager>
		</SidebarContext.Provider>
	);
};
